import {Button, FieldTextArea} from '@bitstillery/common/components/portal'
import {MithrilComponent} from '@bitstillery/mithril'
import {$t, notifier} from '@bitstillery/common/app'
import m from '@bitstillery/mithril'
import {classes, PlausibleCartSource, plausible_event} from '@bitstillery/common/lib/utils'
import {format_money, format_money_with_symbol} from '@bitstillery/common/lib/format'
import {state} from '@bitstillery/mithril'
import {mount_dialog} from '@bitstillery/common/lib/dialog'
import {api} from '@bitstillery/common/app'
import {ProductQuantity} from '@/components/collection/product/quantity/quantity'

import type {CartValidationError} from '@/lib/state'
import {VALIDATION_CATEGORIES} from '@/models/cart'
import {$m, $s} from '@/app'
import type {CartItemState, CartOfferItemLookup} from '@/models/cart'

export interface CartActionsAttrs {
    cart_item: CartItemState
    /** Determines the fix action (quantity/price) to use. Present when item has validation errors. */
    error?: CartValidationError | undefined
    /** Renders the line's price; shown inside the action overlay, next to the buttons, on reveal — so the
     *  in-flow price can stay flush-right (lined up with the totals) while the overlay covers it. */
    price?: () => m.Children
}

interface CartItemActionsOpen {
    key: number | null
}

/**
 * Touch/tablet reveal is exclusive: at most one row's action pill is open at a time. This shared
 * signal holds the open row's `case_artkey` (or null), so opening one row collapses any other.
 */
export const open_actions: CartItemActionsOpen = state({key: null}, 'cart-item-actions-open')

interface CartItemActionsData {
    dialog: {
        loading: boolean
        title: string
        type: string
    }
    /** Keyed by case artkey while its remove or fix is in flight. */
    loading: Record<number, boolean | undefined>
    portal_comment: string
}

export class CartItemActions extends MithrilComponent<CartActionsAttrs> {
    data = state<CartItemActionsData>(
        {
            dialog: {
                loading: false,
                title: $t('checkout.comment.dialog.title'),
                type: 'info',
            },
            loading: {},
            portal_comment: '',
        },
        'cart-item-actions-data',
    )

    view(vnode: m.Vnode<CartActionsAttrs>) {
        // An unavailable item (removed from the offer) has no offer_item, but it
        // still needs its actions — remove and fix-validation both operate on the
        // cart_item, so only the quantity stepper is gated on offer_item below.
        const offer_items: CartOfferItemLookup = $s.cart.offer_items
        const offer_item = offer_items[vnode.attrs.cart_item.case_artkey]

        this.data.dialog.title = $t('checkout.comment.dialog.title', {product_name: vnode.attrs.cart_item.product_name})
        const case_artkey = vnode.attrs.cart_item.case_artkey
        // Read on save, not on render: the dialog outlives the render that opened it, and a new line
        // only gets its artkey once its first write lands.
        const save_comment = async (comment: string) => {
            const order_artkey = $s.cart.artkey
            // The line may have left the cart (another tab, a reload) while the dialog was open.
            const portal_order_item_artkey = $s.cart.items[case_artkey]?.artkey
            if (!order_artkey || !portal_order_item_artkey) {
                return
            }
            await api.put(`portal/orders/${order_artkey}/portal-order-items/${portal_order_item_artkey}/comment`, {comment})
        }
        const expanded = open_actions.key === case_artkey

        return [
            // The action overlay floats at the row's trailing edge (absolute — no layout footprint), so the
            // in-flow price stays flush-right and lined up with the totals. On reveal, the price is shown
            // INSIDE the overlay next to the buttons (it covers the in-flow price).
            <div className={classes('c-cart-item-actions on-chrome', {expanded})}>
                {vnode.attrs.price && <span className='overlay-price'>{vnode.attrs.price()}</span>}
                {/* A validation row is about resolving the issue (fix / remove), so the stepper and the
                    comment action are hidden there — only the fix and a single trash remain. */}
                {!vnode.attrs.error && offer_item && (
                    <ProductQuantity mode='simple' offer_item={offer_item} source={PlausibleCartSource.CART} />
                )}
                {!vnode.attrs.error && (
                    <Button
                        className={vnode.attrs.cart_item.portal_comment ? 'dropdown-header' : 'inactive dropdown-header'}
                        icon={vnode.attrs.cart_item.portal_comment ? 'commentEdit' : 'comment'}
                        onclick={() => {
                            const mounted = mount_dialog({
                                body: () => {
                                    return (
                                        <>
                                            <FieldTextArea
                                                model={this.data.$portal_comment}
                                                placeholder={$t('checkout.comment.dialog.placeholder_description', {
                                                    product_name: vnode.attrs.cart_item.product_name,
                                                })}
                                            />
                                            <div className='action-controls'>
                                                {vnode.attrs.cart_item.portal_comment && (
                                                    <Button
                                                        className='mr-1'
                                                        icon='trash'
                                                        type='warning'
                                                        onclick={async () => {
                                                            this.data.portal_comment = ''
                                                            vnode.attrs.cart_item.portal_comment = ''
                                                            await save_comment('')
                                                            notifier.notify(
                                                                $t('notifications.comment_deleted'),
                                                                'warning',
                                                                undefined,
                                                                'comment',
                                                            )
                                                            mounted.close()
                                                            $m.cart.set_cart_marker(vnode.attrs.cart_item.case_artkey)
                                                        }}
                                                        tip={$t('checkout.comment.action.delete_tip')}
                                                        variant='cta'
                                                    />
                                                )}

                                                <Button
                                                    disabled={!this.data.portal_comment}
                                                    icon='checked'
                                                    text={$t('checkout.comment.dialog.btn_save')}
                                                    type='info'
                                                    variant='cta'
                                                    onclick={async () => {
                                                        vnode.attrs.cart_item.portal_comment = this.data.portal_comment
                                                        await save_comment(this.data.portal_comment)
                                                        notifier.notify(
                                                            $t('notifications.comment_stored'),
                                                            'info',
                                                            undefined,
                                                            'comment',
                                                        )
                                                        mounted.close()
                                                        $m.cart.set_cart_marker(vnode.attrs.cart_item.case_artkey)
                                                    }}
                                                />
                                            </div>
                                        </>
                                    )
                                },
                                className: 'dialog-portal comment-dialog',
                                model: this.data.dialog,
                                onclose: () => {
                                    this.data.portal_comment = vnode.attrs.cart_item.portal_comment ?? ''
                                },
                                onopen: () => {
                                    this.data.portal_comment = vnode.attrs.cart_item.portal_comment ?? ''
                                },
                            })
                        }}
                        size='s'
                        type={vnode.attrs.cart_item.portal_comment ? 'info' : 'default'}
                        tip={() => {
                            if (vnode.attrs.cart_item.portal_comment) {
                                return $t('checkout.comment.action.modify_tip')
                            }
                            return $t('checkout.comment.action.add_tip')
                        }}
                        variant='toggle'
                    />
                )}
                <Button
                    className='btn-remove'
                    icon='trash'
                    // Make sure the trash button is only clicked once...
                    disabled={this.data.loading[vnode.attrs.cart_item.case_artkey]}
                    onclick={async () => {
                        this.data.loading[vnode.attrs.cart_item.case_artkey] = true
                        for (const error_category of $m.cart.error_categories()) {
                            const errors: Record<string, CartValidationError | undefined> = $s.cart.errors[error_category]
                            if (errors[vnode.attrs.cart_item.case_artkey]) {
                                delete errors[vnode.attrs.cart_item.case_artkey]
                            }
                        }

                        const cart_update_status = await $m.cart.update_cart(
                            {
                                number_of_cases: 0,
                                offer_item_artkey: vnode.attrs.cart_item.offer_item_artkey,
                            },
                            vnode.attrs.cart_item.case_artkey,
                            false,
                        )

                        plausible_event(cart_update_status.action, {
                            props: {
                                alcohol: vnode.attrs.cart_item.bottle_alcohol_percentage,
                                cases: cart_update_status.amount,
                                company_type: $s.identity.user.company_type,
                                customs_status: vnode.attrs.cart_item.case_customs_status,
                                product_category: vnode.attrs.cart_item.product_category,
                                product_name: vnode.attrs.cart_item.product_name,
                                source: PlausibleCartSource.CART,
                                volume: vnode.attrs.cart_item.bottle_volume,
                            },
                            revenue: {
                                amount:
                                    Math.round(
                                        Number(cart_update_status.amount) * Number(vnode.attrs.cart_item.list_price) * 100,
                                    ) / 100,
                                currency: $s.identity.user.currency,
                            },
                        })
                        delete this.data.loading[vnode.attrs.cart_item.case_artkey]
                    }}
                    size='s'
                    tip={$t('cart.actions.minus_all', {product: vnode.attrs.cart_item.product_name})}
                    type='warning'
                    variant='toggle'
                />

                {(() => {
                    const error = vnode.attrs.error
                    if (!error) return null
                    return (
                        <Button
                            className='btn-fix-validation'
                            disabled={this.data.loading[vnode.attrs.cart_item.case_artkey]}
                            icon='progress_wrench'
                            onclick={async () => {
                                this.data.loading[vnode.attrs.cart_item.case_artkey] = true
                                const validation_error_type = error.validation_error
                                if (VALIDATION_CATEGORIES.QUANTITY.includes(validation_error_type)) {
                                    await $m.cart.confirm_suggestion_quantity(vnode.attrs.cart_item.case_artkey)
                                } else if (VALIDATION_CATEGORIES.PRICE.includes(validation_error_type)) {
                                    await $m.cart.confirm_suggestion_price_up(vnode.attrs.cart_item.case_artkey)
                                }
                                delete this.data.loading[vnode.attrs.cart_item.case_artkey]
                            }}
                            size='s'
                            tip={() => {
                                const offer_items: CartOfferItemLookup = $s.cart.offer_items
                                const offer_item = offer_items[vnode.attrs.cart_item.case_artkey]
                                const validation_error_type = error.validation_error
                                if (VALIDATION_CATEGORIES.QUANTITY.includes(validation_error_type)) {
                                    const invalid_value = $m.offer.unit_amount(
                                        error.invalid_value,
                                        vnode.attrs.cart_item.case_number_of_bottles,
                                    )
                                    const valid_value = $m.offer.unit_amount(
                                        error.valid_value,
                                        vnode.attrs.cart_item.case_number_of_bottles,
                                    )
                                    let cart_action
                                    const count = Math.abs(invalid_value - valid_value)
                                    if (valid_value === 0) {
                                        cart_action = 'minus_all'
                                    } else if (valid_value < invalid_value) {
                                        cart_action = 'minus'
                                    } else {
                                        cart_action = 'plus'
                                    }

                                    return $t(`cart.actions.${cart_action}`, {
                                        count,
                                        product: vnode.attrs.cart_item.product_name,
                                        units: $t(`unit.${$s.identity.user.price_preference}`, {count}),
                                    })
                                } else if (VALIDATION_CATEGORIES.PRICE.includes(validation_error_type)) {
                                    const invalid_value = $m.offer.unit_price(
                                        error.invalid_value,
                                        vnode.attrs.cart_item.case_number_of_bottles,
                                    )
                                    const valid_value = $m.offer.unit_price(
                                        error.valid_value,
                                        vnode.attrs.cart_item.case_number_of_bottles,
                                    )

                                    if (!offer_item) {
                                        return $t('cart.actions.price_confirm', {
                                            new_price: format_money(valid_value, $s.identity.user.currency),
                                            old_price: format_money(invalid_value, $s.identity.user.currency),
                                            product: vnode.attrs.cart_item.product_name,
                                            unit: $t(`unit.${$s.identity.user.price_preference}`, {count: 1}),
                                        })
                                    }
                                    return $t('cart.actions.price_confirm', {
                                        new_price: format_money_with_symbol(valid_value, offer_item.currency),
                                        old_price: format_money_with_symbol(invalid_value, offer_item.currency),
                                        product: vnode.attrs.cart_item.product_name,
                                        unit: $t(`unit.${$s.identity.user.price_preference}`, {count: 1}),
                                    })
                                }
                                return ''
                            }}
                            type='info'
                            variant='toggle'
                        />
                    )
                })()}
            </div>,
            // Reveal/collapse handle — shown only on touch/tablet (CSS), where hover is unreliable. It sits
            // in flow at the tail of the row, just after the price (never over it), as a quiet glyph; the
            // overlay opens to its left. Opening a row collapses any other.
            <Button
                className='cart-actions-handle'
                icon={expanded ? 'close' : 'chevronLeft'}
                onclick={() => {
                    open_actions.key = expanded ? null : case_artkey
                }}
                size='s'
                type='unset'
                variant='unset'
            />,
        ]
    }
}
